import { create } from "zustand";
import { useMediaQuery, useTheme } from "@mui/material";
import {
  type DrawerPersistence,
  ephemeralPersistence,
  localStoragePersistence,
} from "./drawerPersistence";

export interface DrawerState {
  isOpen: boolean;
  show: () => void;
  hide: () => void;
  toggle: () => void;
}

/**
 * Build a drawer store whose open/closed state is loaded from — and saved
 * back to — the injected persistence adapter. Every mutation goes through
 * `commit`, so persistence happens in exactly one place, one format.
 */
export function createDrawerStore(persistence: DrawerPersistence) {
  return create<DrawerState>((set, get) => {
    const commit = (isOpen: boolean) => {
      persistence.save(isOpen);
      set({ isOpen });
    };
    return {
      isOpen: persistence.load(),
      show: () => commit(true),
      hide: () => commit(false),
      toggle: () => commit(!get().isOpen),
    };
  });
}

// Desktop drawer persists across sessions; mobile drawer is ephemeral.
const useDesktopDrawerStore = createDrawerStore(localStoragePersistence("drawer-desktop-open"));
const useMobileDrawerStore = createDrawerStore(ephemeralPersistence());

/**
 * Hook to manage drawer state (mobile or desktop).
 *
 * Automatically returns the appropriate drawer state based on screen size.
 * Desktop state persists to localStorage, mobile state is ephemeral.
 */
export function useDrawer(): DrawerState {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const desktopDrawer = useDesktopDrawerStore();
  const mobileDrawer = useMobileDrawerStore();

  return isMobile ? mobileDrawer : desktopDrawer;
}
